import { useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Show } from "shared/components";
import { ComposerForm } from "./ComposerForm";
import { ComposerFooterRow } from "./ComposerFooterRow";
import { EmailRoutingSelect } from "./EmailRoutingSelect";
import { SmsRoutingSelect } from "./SmsRoutingSelect";
import {
  recipientKey,
  type Recipient,
  type MultiRecipientControls
} from "./multi-recipient";
import { SendEmailTemplateDialog } from "./SendEmailTemplateDialog";
import { ReplyBanner } from "./ReplyBanner";
import { VoiceComposer } from "./VoiceComposer";
import { CallNumberFields } from "./CallNumberFields";
import { useCallSelection } from "./useCallSelection";
import { useSoftphone } from "softphone";
import { type ComposerTab, type ComposerFormChannel } from "./constants";
import { useCrmContact } from "../api/crm";
import {
  shouldShowChannelTabs,
  useComposerTabs
} from "../hooks/useComposerTabs";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { cx } from "shared/utils/classnames";
import { useInboxContext } from "../context/useInboxContext";
import { useAuthContext, useVoiceEnabled } from "providers/Auth";
import type { EmailTemplate } from "../../settings-communication/api/types";
import type { ReplyTarget } from "../types";

interface MessageComposerProps {
  conversationId?: string;
  contactId?: string;
  draftContactId?: number;
  /**
   * Selected channel. Owned by the host: on /inbox, InboxLayout (`?tab=`, else
   * the user's remembered chip); in the pop-up, its own chip state.
   */
  activeTab: ComposerTab;
  onTabChange: (tab: ComposerTab) => void;
  onMessageSent?: () => void;
  replyTarget?: ReplyTarget | null;
  onCancelReply?: () => void;
  /** Hides the channel tab row when the host renders its own (the pop-up's chips). */
  hideTabs?: boolean;
  // Reports the currently selected SMS "from" number up so sibling components
  // (e.g. the suggestion card) can send from the same number.
  onSmsSelectionChange?: (phoneNumberId: string, toPhone: string) => void;
}

/* eslint-disable sonarjs/cognitive-complexity -- unified composer host: email + sms + voice + chat + AI suggestions */
export const MessageComposer = ({
  conversationId,
  contactId,
  draftContactId,
  activeTab,
  onTabChange,
  onMessageSent,
  replyTarget,
  onCancelReply,
  hideTabs = false,
  onSmsSelectionChange
}: MessageComposerProps) => {
  const { type } = useAuthContext();
  const isVoiceEnabled = useVoiceEnabled();
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);

  const {
    visibleTabs,
    chatTabAvailable,
    hasEligibleApplications,
    canonicalApplicationId,
    inviteModal
  } = useComposerTabs(contactId);

  const [selectedMailbox, setSelectedMailbox] = useState<string>("");
  const [selectedToEmail, setSelectedToEmail] = useState<string>("");
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>("");
  const [selectedToPhone, setSelectedToPhone] = useState<string>("");
  const [isToPhoneOptedOut, setIsToPhoneOptedOut] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  const [extraRecipients, setExtraRecipients] = useState<Recipient[]>([]);
  const [primaryExcluded, setPrimaryExcluded] = useState(false);

  const addRecipient = useCallback((recipient: Recipient) => {
    setExtraRecipients((prev) =>
      prev.some(
        (existing) =>
          recipientKey(existing.contactId, existing.address) ===
          recipientKey(recipient.contactId, recipient.address)
      )
        ? prev
        : [...prev, recipient]
    );
  }, []);

  const removeRecipient = useCallback((recipient: Recipient) => {
    setExtraRecipients((prev) => {
      const next = prev.filter(
        (existing) =>
          recipientKey(existing.contactId, existing.address) !==
          recipientKey(recipient.contactId, recipient.address)
      );
      if (next.length === 0) setPrimaryExcluded(false);
      return next;
    });
  }, []);

  const multiRecipientControls = useMemo<MultiRecipientControls>(
    () => ({
      extraRecipients,
      onAddRecipient: addRecipient,
      onRemoveRecipient: removeRecipient,
      primaryExcluded,
      onPrimaryExcludedChange: setPrimaryExcluded
    }),
    [extraRecipients, addRecipient, removeRecipient, primaryExcluded]
  );

  const handleTemplateSelect = useCallback((template: EmailTemplate) => {
    setSelectedTemplateId(template.id);
  }, []);

  const handleTemplateDialogClose = useCallback(() => {
    setSelectedTemplateId(null);
  }, []);
  const { data: contact } = useCrmContact(contactId || "");
  const routingKey = conversationId ?? contactId;
  const {
    state: { selectedSubject },
    actions: { clearTargetMessage }
  } = useInboxContext();

  // The chat is sent about the subject open in the Details panel: the contact's
  // applicant on that application (so the SMS mirrors to THIS contact, not the
  // application's primary applicant), falling back to their most-recent app.
  const activeApplicationId =
    selectedSubject?.type === "application"
      ? Number(selectedSubject.id)
      : canonicalApplicationId;
  const recipientApplicantId = useMemo(
    () =>
      contact?.applicants?.find(
        (applicant) => applicant.application_id === activeApplicationId
      )?.id,
    [contact?.applicants, activeApplicationId]
  );

  useEffect(() => {
    setExtraRecipients([]);
    setPrimaryExcluded(false);
  }, [conversationId, contactId, activeTab]);

  const handleMessageSent = useCallback(() => {
    setExtraRecipients([]);
    setPrimaryExcluded(false);
    onMessageSent?.();
  }, [onMessageSent]);

  const handleTabChange = useCallback(
    (tab: ComposerTab) => {
      clearTargetMessage();
      onTabChange(tab);
    },
    [onTabChange, clearTargetMessage]
  );

  const handleEmailSelectionChange = useCallback(
    (mailboxId: string, toEmail: string) => {
      setSelectedMailbox(mailboxId);
      setSelectedToEmail(toEmail);
    },
    []
  );

  const handleSmsSelectionChange = useCallback(
    (phoneNumberId: string, toPhone: string, isToOptedOut: boolean) => {
      setSelectedPhoneId(phoneNumberId);
      setSelectedToPhone(toPhone);
      setIsToPhoneOptedOut(isToOptedOut);
      onSmsSelectionChange?.(phoneNumberId, toPhone);
    },
    [onSmsSelectionChange]
  );

  const contactName = useMemo(() => {
    if (!contact) return "";
    // Filter nulls so template literal doesn't coerce them to "null".
    return [contact.first_name, contact.last_name]
      .filter((part): part is string => !!part && part.trim().length > 0)
      .join(" ")
      .trim();
  }, [contact]);

  const isReply = !!replyTarget;
  const chatUsable = chatTabAvailable && hasEligibleApplications;
  const requestedTab: ComposerTab = isReply ? "email" : activeTab;
  // Resolve to a tab that's actually usable: email when available, else the
  // first visible tab (e.g. Chat for native-chat-only partners with no email).
  // A LOCKED email or Call tab (no communication_email / communication_phone)
  // is visible only for the upsell and is skipped here like a hidden one, so a
  // reply or a stale `?tab=` can't land the composer on a channel the partner
  // cannot send from — nor on the "Make a call" form under a locked Call tab.
  const isLocked = (tab: (typeof visibleTabs)[number]) =>
    (tab.value === "email" || tab.value === "voice") && tab.disabled;
  const emailLocked = visibleTabs.some(
    (tab) => tab.value === "email" && tab.disabled
  );
  const voiceLocked = visibleTabs.some(
    (tab) => tab.value === "voice" && tab.disabled
  );
  const emailUsable =
    visibleTabs.some((tab) => tab.value === "email") && !emailLocked;
  const fallbackTab: ComposerTab | undefined = emailUsable
    ? "email"
    : visibleTabs.find((tab) => !isLocked(tab))?.value;
  const requestedUsable =
    visibleTabs.some((tab) => tab.value === requestedTab) &&
    !(requestedTab === "email" && emailLocked) &&
    !(requestedTab === "voice" && (voiceLocked || !isVoiceEnabled)) &&
    !(requestedTab === "chat" && !chatUsable);
  const effectiveTab: ComposerTab | undefined = requestedUsable
    ? requestedTab
    : fallbackTab;

  const { activeCall } = useSoftphone();
  const callSelection = useCallSelection(
    contactId || "",
    effectiveTab === "voice" && !activeCall
  );

  // The tab row is also dropped for a partner without CRM, whose single Chat
  // tab has nothing to switch to (CRM-1325).
  const showChannelTabs =
    !hideTabs &&
    shouldShowChannelTabs({ isBoomCRM: type.isBoomCRM, tabs: visibleTabs });
  // The footer holds the channel tabs and the per-channel routing selects. The
  // pop-up renders its own chips and hides the tabs (CRM-1133), which can leave
  // the row with nothing in it — skip it then instead of drawing an empty 80px
  // strip.
  // In the pop-up the From/To selects render in the participants block under
  // the chips (portaled below), not in the composer footer.
  const showEmailRouting = effectiveTab === "email" && !isCompact;
  const showSmsRouting = effectiveTab === "sms" && !isCompact;
  const showCallNumberFields =
    effectiveTab === "voice" &&
    !activeCall &&
    callSelection.toOptions.length > 0;
  const hasComposerFooter =
    (!isReply && showChannelTabs) ||
    showEmailRouting ||
    showSmsRouting ||
    showCallNumberFields;

  if (!conversationId && draftContactId === undefined) return null;

  // No channel is available to this partner. The fallback used to default to
  // "email", which mounted the email composer for partners without the email
  // entitlement (CRM-1132) — render no composer instead.
  if (!effectiveTab) return null;

  return (
    <div
      className={cx(
        "bg-white border-t border-alpha-100",
        // The design's pop-up footer insets the input card; /inbox's composer
        // spans the pane and pads itself (12px top, 8px to the SMS note).
        isCompact ? "px-5 pt-3 pb-2" : ""
      )}
    >
      {isReply && onCancelReply ? (
        <ReplyBanner
          target={replyTarget}
          onCancel={onCancelReply}
          compact={isCompact}
        />
      ) : null}

      {/* Pop-up only: the routing selects live in the header's participants
          block. Portaling (rather than moving the components) keeps their
          selection state and default-picking effects mounted even while the
          block is collapsed, so sends always have a From/To. */}
      {isCompact && embed?.participantsEl && effectiveTab === "email"
        ? createPortal(
            <EmailRoutingSelect
              key={routingKey}
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleEmailSelectionChange}
              recommendSender
              menuPlacement="auto"
              compact
            />,
            embed.participantsEl
          )
        : null}
      {isCompact && embed?.participantsEl && effectiveTab === "sms"
        ? createPortal(
            <SmsRoutingSelect
              key={routingKey}
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleSmsSelectionChange}
              recommendSender
              menuPlacement="auto"
              compact
            />,
            embed.participantsEl
          )
        : null}

      <Show if={hasComposerFooter}>
        <ComposerFooterRow
          key={conversationId ?? `draft-${draftContactId}`}
          tabs={visibleTabs}
          active={effectiveTab}
          onTabChange={handleTabChange}
          showTabs={!isReply && showChannelTabs}
        >
          {showEmailRouting ? (
            <EmailRoutingSelect
              key={routingKey}
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleEmailSelectionChange}
              recommendSender
              multiRecipient={isReply ? undefined : multiRecipientControls}
            />
          ) : null}

          {showSmsRouting ? (
            <SmsRoutingSelect
              key={routingKey}
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleSmsSelectionChange}
              recommendSender
              multiRecipient={isReply ? undefined : multiRecipientControls}
            />
          ) : null}

          {showCallNumberFields ? (
            <CallNumberFields
              selection={callSelection}
              menuPlacement="top"
              inline
            />
          ) : null}
        </ComposerFooterRow>
      </Show>

      {effectiveTab === "voice" ? (
        <VoiceComposer contactId={contactId} selection={callSelection} />
      ) : (
        <ComposerForm
          channel={effectiveTab as ComposerFormChannel}
          conversationId={conversationId}
          draftContactId={draftContactId}
          mailboxId={selectedMailbox}
          toEmail={selectedToEmail}
          toName={contactName}
          phoneNumberId={selectedPhoneId}
          toPhone={selectedToPhone}
          applicationId={activeApplicationId}
          recipientApplicantId={recipientApplicantId}
          isToOptedOut={isToPhoneOptedOut}
          contactId={contactId ? Number(contactId) : undefined}
          replyToMessageId={replyTarget?.messageId}
          replySubject={replyTarget?.subject}
          extraRecipients={extraRecipients}
          primaryExcluded={primaryExcluded}
          onMessageSent={handleMessageSent}
          onTemplateSelect={handleTemplateSelect}
        />
      )}

      {emailUsable &&
      selectedTemplateId !== null &&
      (conversationId || draftContactId !== undefined) ? (
        <SendEmailTemplateDialog
          key={selectedTemplateId}
          open
          onClose={handleTemplateDialogClose}
          templateId={selectedTemplateId}
          conversationId={conversationId}
          draftContactId={draftContactId}
          contactId={contactId}
          replyToMessageId={replyTarget?.messageId}
          onMessageSent={onMessageSent}
        />
      ) : null}

      {inviteModal}
    </div>
  );
};
